import { Chip, Drawer, IconButton, makeStyles, Tooltip, Typography } from "@material-ui/core";
import { GridColDef } from "@material-ui/data-grid";
import React, { useMemo, useState } from "react";
import moment from "moment";
import { MdChevronLeft, MdChevronRight, MdClose, MdOpenInNew } from "react-icons/md";
import { useHistory, useLocation } from "react-router-dom";
import { useQuery } from "../../../../hooks/useQuery";
import { Account, AccountStatus, accountsForSelectedCompany, buildAccounts, groupLabel, isAdmin, statusLabels } from "../../accounts";
import { EntityTable, filterBySearch } from "../../components/EntityTable";
import { PageHeader } from "../../components/PageHeader";
import { SysadminData, useSysadminData } from "../../SysadminData";
import { parseServiceDate, sysadminPaths } from "../../sysadminUtils";
import { AddMembersDialog } from "./AddMembersDialog";
import { InviteUserDialog } from "./InviteUserDialog";
import { Prefill, UserEditor } from "./UserAccount";

const useStyles = makeStyles(theme => ({
    chips: {
        display: "flex",
        gap: 4,
        overflow: "hidden",
    },
    muted: {
        color: theme.palette.text.secondary,
    },
    groupChip: {
        maxWidth: 140,
        flexShrink: 1,
        minWidth: 0,
    },
    ellipsis: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    panel: {
        width: 640,
        maxWidth: "100vw",
        padding: theme.spacing(3),
        background: theme.palette.grey[50],
    },
    position: {
        alignSelf: "center",
        marginRight: theme.spacing(1),
        whiteSpace: "nowrap",
    },
}));

const statusColors: Record<AccountStatus, { background: string; color: string }> = {
    ok: { background: "#e6f4ea", color: "#1e6b34" },
    unverified: { background: "#fff4e0", color: "#8a5300" },
    unplaced: { background: "#e8f0fe", color: "#1a4fa0" },
    missingAccount: { background: "#fde7e7", color: "#a11a1a" },
};

export const StatusChip: React.FC<{ status: AccountStatus }> = ({ status }) => (
    <Chip size="small" label={statusLabels[status]} style={{ ...statusColors[status], fontWeight: 600 }} />
);

const GroupsCell: React.FC<{ groups: string[] }> = ({ groups }) => {
    const classes = useStyles();
    if (groups.length === 0) return <Typography variant="body2" className={classes.muted}>No groups</Typography>;
    const shown = groups.slice(0, 2);
    return (
        <Tooltip title={groups.map(groupLabel).join(", ")}>
            <div className={classes.chips}>
                {shown.map(group => <Chip key={group} size="small" variant="outlined" className={classes.groupChip} label={groupLabel(group)} />)}
                {groups.length > shown.length && <Chip size="small" variant="outlined" label={`+${groups.length - shown.length}`} />}
            </div>
        </Tooltip>
    );
};

const TextCell: React.FC<{ text: string }> = ({ text }) => {
    const classes = useStyles();
    return text ? <Tooltip title={text}><span className={classes.ellipsis}>{text}</span></Tooltip> : <></>;
};

function adminForText(data: SysadminData, account: Account): string {
    const names = [
        ...account.adminFor.companies.map(id => data.companyById(id)?.companyName || "Unnamed company"),
        ...account.adminFor.offices.map(id => data.officeById(id)?.officeName || "Unnamed office"),
        ...account.adminFor.eaterGroups.map(id => data.eaterGroupById(id)?.eaterGroupName || "Unnamed eater group"),
    ];
    return names.join(", ");
}

interface AccountRow {
    email: string;
    name: string;
    status: AccountStatus;
    groups: string[];
    placement: string;
    adminFor: string;
    lastChanged?: Date;
    account: Account;
}

export type UserFilter = "all" | "attention" | AccountStatus | "repair" | "admins";

const filters: { value: UserFilter; label: string; test: (a: Account) => boolean; newestFirst?: boolean }[] = [
    { value: "all", label: "All", test: () => true },
    { value: "attention", label: "Needs attention", test: a => a.status !== "ok" || a.groupsNeedRepair, newestFirst: true },
    { value: "unverified", label: "Unverified", test: a => a.status === "unverified", newestFirst: true },
    { value: "unplaced", label: "No eater group", test: a => a.status === "unplaced", newestFirst: true },
    { value: "repair", label: "Broken user groups", test: a => a.groupsNeedRepair },
    { value: "missingAccount", label: "Missing account", test: a => a.status === "missingAccount" },
    { value: "admins", label: "Admins", test: isAdmin },
];

const columns = (showPlacement: boolean): GridColDef[] => [
    { field: "email", headerName: "Email", flex: 1.2, minWidth: 220, renderCell: p => <TextCell text={(p.row as AccountRow).email} /> },
    { field: "status", headerName: "Status", width: 150, renderCell: p => <StatusChip status={(p.row as AccountRow).status} /> },
    {
        field: "groups", headerName: "User groups", flex: 1.2, minWidth: 230, sortable: false,
        renderCell: p => <GroupsCell groups={(p.row as AccountRow).groups} />,
    },
    { field: "placement", headerName: "Placement", flex: 1.2, minWidth: 200, hide: !showPlacement, renderCell: p => <TextCell text={(p.row as AccountRow).placement} /> },
    { field: "adminFor", headerName: "Admin for", flex: 1, minWidth: 160, renderCell: p => <TextCell text={(p.row as AccountRow).adminFor} /> },
    {
        field: "lastChanged", headerName: "Last changed", type: "dateTime", width: 130,
        valueFormatter: p => (p.value ? moment(p.value as Date).format("DD-MM-YYYY") : ""),
    },
    { field: "name", headerName: "Name", width: 160, hide: true },
];

/** Side panel with a user, with buttons to step through the users in the list. */
export const UserPanel: React.FC<{ email: string; prefill?: Prefill; emails: string[]; onOpen: (email: string) => void; onClose: () => void }> = ({
    email, prefill, emails, onOpen, onClose,
}) => {
    const classes = useStyles();
    const history = useHistory();
    const index = emails.indexOf(email);
    const previous = index > 0 ? emails[index - 1] : undefined;
    const next = index >= 0 && index < emails.length - 1 ? emails[index + 1] : undefined;

    return (
        <Drawer anchor="right" open onClose={onClose} classes={{ paper: classes.panel }}>
            <UserEditor
                key={email}
                email={email}
                variant="panel"
                prefill={prefill}
                onDeleted={onClose}
                panelControls={
                    <>
                        {index >= 0 && (
                            <Typography variant="caption" color="textSecondary" className={classes.position}>
                                {index + 1} of {emails.length}
                            </Typography>
                        )}
                        <Tooltip title="Previous user"><span>
                            <IconButton size="small" disabled={!previous} onClick={() => previous && onOpen(previous)} aria-label="Previous user"><MdChevronLeft /></IconButton>
                        </span></Tooltip>
                        <Tooltip title="Next user"><span>
                            <IconButton size="small" disabled={!next} onClick={() => next && onOpen(next)} aria-label="Next user"><MdChevronRight /></IconButton>
                        </span></Tooltip>
                        <Tooltip title="Open as page">
                            <IconButton size="small" onClick={() => history.push(sysadminPaths.detail("users", email))} aria-label="Open as page"><MdOpenInNew /></IconButton>
                        </Tooltip>
                        <Tooltip title="Close">
                            <IconButton size="small" onClick={onClose} aria-label="Close"><MdClose /></IconButton>
                        </Tooltip>
                    </>
                }
            />
        </Drawer>
    );
};

const PANEL_PARAMS = ["user", "officeId", "eaterGroupId"];

/**
 * Login accounts with their user groups and placement. Clicking a user opens it in a side panel (`?user=<email>`).
 * Limited to one eater group on the eater group page, otherwise to the company chosen in the selector.
 */
export const UsersTable: React.FC<{ eaterGroupId?: string; officeId?: string; title?: string }> = ({ eaterGroupId, officeId, title }) => {
    const data = useSysadminData();
    const history = useHistory();
    const location = useLocation();
    const query = useQuery();
    const [filter, setFilter] = useState<UserFilter>(() => (filters.some(f => f.value === query.get("filter")) ? query.get("filter") as UserFilter : "all"));
    const [search, setSearch] = useState("");
    const [adding, setAdding] = useState(false);
    const [inviting, setInviting] = useState(false);

    const accounts = useMemo(() => buildAccounts(data), [data]);
    const scoped = useMemo(
        () => (eaterGroupId ? accounts.filter(a => a.user?.eaterGroupId === eaterGroupId) : accountsForSelectedCompany(data, accounts)),
        [accounts, data, eaterGroupId]
    );
    const activeFilter = filters.find(f => f.value === filter) ?? filters[0];

    const rows: AccountRow[] = useMemo(() => scoped
        .filter(activeFilter.test)
        .map(account => {
            const office = data.officeById(account.user?.officeId);
            const company = data.companyById(account.companyId);
            const placement = [
                // The company is already given by the selector
                !data.selectedCompanyId ? company?.companyName : undefined,
                office?.officeName,
                data.eaterGroupById(account.user?.eaterGroupId)?.eaterGroupName,
            ].filter(Boolean).join(" › ");
            return {
                email: account.email,
                name: account.name,
                status: account.status,
                groups: account.groups,
                placement,
                adminFor: adminForText(data, account),
                lastChanged: parseServiceDate(account.serviceUser?.versionDateTime),
                account,
            };
        })
        .sort((a, b) => activeFilter.newestFirst
            ? (b.lastChanged?.getTime() ?? 0) - (a.lastChanged?.getTime() ?? 0) || a.email.localeCompare(b.email)
            : a.email.localeCompare(b.email)),
    [scoped, activeFilter, data]);

    const searchText = (row: AccountRow) => [row.email, row.name, row.placement, row.adminFor, ...row.groups, ...row.groups.map(groupLabel)].join(" ");
    const visibleEmails = filterBySearch(rows, search, searchText).map(r => r.email);

    const openEmail = query.get("user") ?? undefined;
    const prefillOffice = query.get("officeId");
    const prefill = prefillOffice ? { officeId: prefillOffice, eaterGroupId: query.get("eaterGroupId") ?? "" } : undefined;

    function setPanel(email?: string, extra?: Record<string, string>) {
        const params = new URLSearchParams(location.search);
        PANEL_PARAMS.forEach(key => params.delete(key));
        if (email) params.set("user", email);
        Object.entries(extra ?? {}).forEach(([key, value]) => params.set(key, value));
        history.push({ pathname: location.pathname, search: params.toString() });
    }

    function changeFilter(value: UserFilter) {
        setFilter(value);
        const params = new URLSearchParams(location.search);
        if (value === "all") params.delete("filter"); else params.set("filter", value);
        history.replace({ pathname: location.pathname, search: params.toString() });
    }

    const filterChips = eaterGroupId ? undefined : filters.map(f => {
        const count = scoped.filter(f.test).length;
        if (count === 0 && f.value !== "all" && f.value !== filter) return null;
        return (
            <Chip
                key={f.value}
                label={`${f.label} (${count})`}
                color={filter === f.value ? "primary" : "default"}
                variant={filter === f.value ? "default" : "outlined"}
                onClick={() => changeFilter(f.value)}
            />
        );
    });

    const selectedName = !eaterGroupId && data.selectedCompanyId ? data.companyById(data.selectedCompanyId)?.companyName : undefined;
    const placeHere = eaterGroupId ? { officeId: officeId ?? "", eaterGroupId } : undefined;

    return (
        <>
            <EntityTable<AccountRow>
                title={title}
                rows={rows}
                columns={columns(!eaterGroupId)}
                getRowId={row => row.email}
                searchText={searchText}
                searchPlaceholder="Search email, group, office, eater group"
                search={search}
                onSearchChange={setSearch}
                selectedRowId={openEmail}
                onRowClick={row => setPanel(row.email)}
                createLabel={eaterGroupId ? "Add users" : "Invite user"}
                onCreate={() => (eaterGroupId ? setAdding(true) : setInviting(true))}
                loading={data.loading && data.serviceUsers.length === 0}
                error={data.error}
                onRetry={data.reload}
                filters={filterChips}
                emptyText={eaterGroupId ? "This eater group has no users yet" : selectedName ? `No users found for ${selectedName}` : "No users yet"}
            />
            {eaterGroupId && adding && (
                <AddMembersDialog
                    eaterGroupId={eaterGroupId}
                    onClose={() => setAdding(false)}
                    onInvite={() => { setAdding(false); setInviting(true); }}
                />
            )}
            {inviting && (
                <InviteUserDialog
                    prefill={placeHere}
                    onClose={() => setInviting(false)}
                    onInvited={email => { setInviting(false); setPanel(email); }}
                    onOpenExisting={email => { setInviting(false); setPanel(email); }}
                />
            )}
            {openEmail && (
                <UserPanel
                    email={openEmail}
                    prefill={prefill}
                    emails={visibleEmails}
                    onOpen={email => setPanel(email)}
                    onClose={() => setPanel()}
                />
            )}
        </>
    );
};

export const Users: React.FC = () => {
    const data = useSysadminData();
    const selectedName = data.selectedCompanyId ? data.companyById(data.selectedCompanyId)?.companyName : undefined;
    return (
        <div>
            <PageHeader
                title="Users"
                subtitle={selectedName
                    ? `Users placed in or administrating ${selectedName}. Users without a company are always shown so they can be placed.`
                    : "All login accounts. Click a user to change user groups, placement and what they administrate."}
            />
            <UsersTable />
        </div>
    );
};
